/* import __COLOCATED_TEMPLATE__ from './ask-a-teammate.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { get } from 'embercom/lib/ajax';
import type { FinSetupBehaviorSaveParams } from 'embercom/lib/operator/fin/types';
import type IntlService from 'ember-intl/services/intl';
import type Profile from 'embercom/models/ai-agent/profile';
import PredicateGroup from 'predicates/models/predicate-group';

const FIN_EMAIL_HITL_FF = 'fin-email-hitl';
const MIN_THRESHOLD = 10;
const DEFAULT_THRESHOLD = 50;
const HOUR = 3600;
const DAY = 86400;
const MIN_TIMEOUT_SECONDS = HOUR;
const MAX_TIMEOUT_SECONDS = 30 * DAY;
const DEFAULT_TIMEOUT_SECONDS = 24 * HOUR;

type Option = 'on' | 'off';

interface Args {
  accordion: $TSFixMe;
  ruleset: $TSFixMe;
  partialSave: (
    params: FinSetupBehaviorSaveParams,
    options?: {
      messages?: { success?: string; failure?: string };
      rethrow?: boolean;
    },
  ) => Promise<void> | void;
}

interface Signature {
  Args: Args;
}

export default class AskATeammate extends Component<Signature> {
  @service declare appService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare attributeService: $TSFixMe;
  @service declare store: $TSFixMe;

  @tracked selectedOption: Option = this.behavior.hitlSettings?.enabled ? 'on' : 'off';
  @tracked selectedThreshold: number = Math.max(
    MIN_THRESHOLD,
    this.behavior.hitlSettings?.confidence_threshold ?? DEFAULT_THRESHOLD,
  );
  @tracked selectedAssigneeId: string | null =
    this.behavior.hitlSettings?.assignee_id != null
      ? String(this.behavior.hitlSettings.assignee_id)
      : null;
  @tracked selectedTimeoutSeconds: number =
    this.behavior.hitlSettings?.timeout_seconds ?? DEFAULT_TIMEOUT_SECONDS;
  @tracked predicateGroup: any = null;
  @tracked isSaving = false;
  @tracked avgDailyEmailConversations: number | null = null;

  private savedOption: Option = this.behavior.hitlSettings?.enabled ? 'on' : 'off';
  private savedThreshold: number = Math.max(
    MIN_THRESHOLD,
    this.behavior.hitlSettings?.confidence_threshold ?? DEFAULT_THRESHOLD,
  );
  private savedAssigneeId: string | null =
    this.behavior.hitlSettings?.assignee_id != null
      ? String(this.behavior.hitlSettings.assignee_id)
      : null;
  private savedTimeoutSeconds: number =
    this.behavior.hitlSettings?.timeout_seconds ?? DEFAULT_TIMEOUT_SECONDS;
  private savedPredicateGroupData: any[] = this.behavior.hitlSettings?.conditions?.predicates ?? [];

  get behavior(): Profile {
    return this.args.ruleset.rulesetLinks.firstObject.object;
  }

  get canUseFinEmailHitl(): boolean {
    return Boolean(this.appService?.app?.canUseFeature?.(FIN_EMAIL_HITL_FF));
  }

  get isEnabled(): boolean {
    return this.selectedOption === 'on';
  }

  get statusLabel(): string {
    if (!this.isEnabled) {
      return this.intl.t('operator.fin.setup.setup-and-go-live.ask-a-teammate.disabled-status');
    }
    return this.intl.t('operator.fin.setup.setup-and-go-live.ask-a-teammate.enabled-status', {
      threshold: this.selectedThreshold,
    });
  }

  get isInteractionDisabled(): boolean {
    return this.isSaving;
  }

  get minTimeoutSeconds(): number {
    return MIN_TIMEOUT_SECONDS;
  }

  get maxTimeoutSeconds(): number {
    return MAX_TIMEOUT_SECONDS;
  }

  get showVolumeHelper(): boolean {
    // New pill design always renders the helper (shows ~0 when no volume data).
    // Old layout hides the helper when the per-threshold estimate rounds to zero.
    return this.canUseFinEmailHitl ? true : this.estimatedConversationsPerDay > 0;
  }

  get estimatedConversationsPerDay(): number {
    let avg = this.avgDailyEmailConversations ?? 0;
    return Math.round(avg * (this.selectedThreshold / 100));
  }

  get attributeGroupList() {
    return this.attributeService.visualBotBuilderConditionalAttributesGroupList ?? [];
  }

  private buildPredicateGroup(rawPredicates?: any[]): any {
    let predicates =
      rawPredicates && rawPredicates.length > 0
        ? PredicateGroup.convertRawPredicates(this.store, rawPredicates)
        : [];
    return this.store.createFragment('predicates/predicate-group', { predicates });
  }

  @action
  setSelectedOption(option: Option) {
    if (this.isInteractionDisabled || option === this.selectedOption) {
      return;
    }
    this.selectedOption = option;
  }

  @action
  setSelectedThreshold(threshold: number) {
    if (this.isInteractionDisabled) {
      return;
    }
    this.selectedThreshold = Math.min(100, Math.max(MIN_THRESHOLD, threshold));
  }

  @action
  setAssigneeId(id: string) {
    if (this.isInteractionDisabled) {
      return;
    }
    this.selectedAssigneeId = id === '0' ? null : id;
  }

  @action
  setTimeoutSeconds(seconds: number) {
    if (this.isInteractionDisabled) {
      return;
    }
    this.selectedTimeoutSeconds = Math.min(
      MAX_TIMEOUT_SECONDS,
      Math.max(MIN_TIMEOUT_SECONDS, seconds),
    );
  }

  @action
  onPredicateGroupChange(predicates: any) {
    if (this.isInteractionDisabled) {
      return;
    }
    this.predicateGroup.set('predicates', [...predicates]);
  }

  @action
  async save(closeAccordion: () => void) {
    if (this.isInteractionDisabled) {
      return;
    }

    this.isSaving = true;

    let predicatesData = this.predicateGroup?.get('predicates')?.serialize() ?? [];
    let hitlSettings: {
      enabled: boolean;
      confidence_threshold?: number;
      assignee_id?: number | null;
      timeout_seconds?: number;
      conditions?: { predicates: any[] } | null;
    } =
      this.selectedOption === 'on'
        ? {
            enabled: true,
            confidence_threshold: this.selectedThreshold,
            assignee_id: this.selectedAssigneeId != null ? Number(this.selectedAssigneeId) : null,
            timeout_seconds: this.selectedTimeoutSeconds,
            conditions: predicatesData.length > 0 ? { predicates: predicatesData } : null,
          }
        : { enabled: false };

    try {
      await this.args.partialSave(
        { hitl_settings: hitlSettings },
        {
          messages: {
            success: this.intl.t(
              'operator.fin.setup.setup-and-go-live.ask-a-teammate.save-success',
            ),
            failure: this.intl.t('operator.fin.setup.setup-and-go-live.ask-a-teammate.save-error'),
          },
          rethrow: true,
        },
      );
      this.savedOption = this.selectedOption;
      this.savedThreshold = this.selectedThreshold;
      this.savedAssigneeId = this.selectedAssigneeId;
      this.savedTimeoutSeconds = this.selectedTimeoutSeconds;
      this.savedPredicateGroupData = this.predicateGroup?.get('predicates')?.serialize() ?? [];
      closeAccordion();
    } catch {
      this.selectedOption = this.savedOption;
      this.selectedThreshold = this.savedThreshold;
      this.selectedAssigneeId = this.savedAssigneeId;
      this.selectedTimeoutSeconds = this.savedTimeoutSeconds;
      this.predicateGroup = this.buildPredicateGroup(this.savedPredicateGroupData);
    } finally {
      this.isSaving = false;
    }
  }

  @action
  revertPendingChanges() {
    if (this.isSaving) {
      return;
    }
    this.selectedOption = this.savedOption;
    this.selectedThreshold = this.savedThreshold;
    this.selectedAssigneeId = this.savedAssigneeId;
    this.selectedTimeoutSeconds = this.savedTimeoutSeconds;
    this.predicateGroup = this.buildPredicateGroup(this.savedPredicateGroupData);
  }

  @action
  close(closeAccordion: () => void) {
    this.revertPendingChanges();
    closeAccordion();
  }

  @action
  initPredicateGroup() {
    if (!this.predicateGroup) {
      this.predicateGroup = this.buildPredicateGroup(
        this.behavior.hitlSettings?.conditions?.predicates,
      );
    }
  }

  @action
  async loadAvgVolume() {
    let appId = this.appService?.app?.id;
    if (!appId) {
      return;
    }

    try {
      let response: { conversations_per_day: number } = await get(
        `/ember/ai_agent/workspace_volume/email_conversations_per_day?app_id=${appId}`,
      );
      let count = response?.conversations_per_day;
      this.avgDailyEmailConversations = Number.isFinite(count) ? count : null;
    } catch (e: unknown) {
      // Volume helper is non-essential; failure hides the sentence without disrupting setup.
      if (e instanceof Error) {
        console.warn('Volume estimate fetch failed:', e.message);
      }
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::Sections::AskATeammate': typeof AskATeammate;
    'operator/fin/setup/sections/ask-a-teammate': typeof AskATeammate;
  }
}
